import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { BillingPlansSelectors } from '@core/billing-plans-data';
import { filterBoolean } from '@core/common-legacy';
import { getQuantityForCompanySize } from '@core/pricing-data';
import type { CustomBillingPlan } from '@core/products-types';

import { getAssignedProduct } from '../helpers/get-assigned-product';
import { getProductSafeSlotsCount } from '../helpers/get-product-safe-slots-count';
import { isPp24 } from '../helpers/is-pp24';
import { ready } from '../helpers/rx-operators';
import {
  PricingFeatureFacade,
  PricingFeatureObservableModel,
} from '../types/pricing-feature.types';

@Injectable({
  providedIn: 'root',
})
export class AssignedProductService {
  public data$ = combineLatest({
    model: this.model$.pipe(ready),
    billingPlans: this.store.select(BillingPlansSelectors.billingPlans).pipe(filterBoolean),
    activePlan: this.store.select(BillingPlansSelectors.activePlan),
  }).pipe(
    map(({ model, billingPlans, activePlan }) => ({
      product: getAssignedProduct(model, billingPlans, activePlan),
      model,
    })),
    tap(({ product, model }) => {
      this.product = product;
      this.productSlots = product ? getProductSafeSlotsCount(product, model) : undefined;
    }),
    map(({ product }) => product),
    shareReplay(1),
  );

  private product: CustomBillingPlan | undefined = undefined;
  private productSlots: number | undefined;

  constructor(
    private facade: PricingFeatureFacade,
    private store: Store,
    private model$: PricingFeatureObservableModel,
  ) {}

  public goToBilling() {
    if (!this.product) return;
    const { id, tier, companySize, billingPeriod } = this.product;

    if (isPp24(tier)) {
      if (!companySize) throw new Error(`companySize is missing from the custom plan id:${id}`);
      const quantity = getQuantityForCompanySize(companySize);
      this.facade.openBilling2024(id, quantity, billingPeriod);
    } else {
      this.productSlots && this.facade.openBilling(id, this.productSlots, billingPeriod);
    }
  }
}
